/**
 * Insert HTML or plain text into the patient report editor without stealing DOM
 * focus (avoids sidebar / Attached Documents UI flicker).
 */

function readEditorHtml(editor) {
  if (typeof editor.getHTML === "function") return editor.getHTML();
  if (typeof editor.getContents === "function") return editor.getContents();
  return "";
}

function syncTranscript(editor, setTranscript) {
  if (typeof setTranscript !== "function") return;
  const newContent = readEditorHtml(editor);
  if (newContent != null) setTranscript(newContent);
}

/**
 * TipTap JSON text node — avoids HTML parse / bare-<span> insertText bug.
 * @param {string} text
 * @returns {{ type: "text", text: string }}
 */
export function toTipTapTextContent(text) {
  return { type: "text", text: String(text ?? "") };
}

/**
 * Insert at the current document selection without calling editor.focus().
 */
function insertAtSelectionWithoutFocus(editor, content) {
  if (!editor?.chain || !editor?.state?.selection) {
    return false;
  }

  const { from, to } = editor.state.selection;
  const hasSelection = from !== to;

  let chain = editor.chain();
  if (hasSelection) {
    chain = chain.deleteRange({ from, to });
  }

  const insertPos = from;
  const inserted = chain
    .insertContentAt(insertPos, content, { updateSelection: false })
    .run();

  return inserted !== false;
}

/**
 * Append at the end of the document without focusing the editor.
 */
function appendAtEndWithoutFocus(editor, content) {
  if (!editor?.chain || !editor?.state?.doc) {
    return false;
  }

  let docSize = 0;
  try {
    docSize = editor.state.doc.content.size;
  } catch {
    return false;
  }

  const inserted = editor
    .chain()
    .insertContentAt(docSize, content, { updateSelection: false })
    .run();

  return inserted !== false;
}

function appendViaSetContents(editor, html) {
  const currentContent = readEditorHtml(editor);
  const separator = currentContent && currentContent.trim() ? "\n\n" : "";
  const newContent = currentContent + separator + html;

  if (typeof editor.setContents === "function") {
    editor.setContents(newContent);
    return true;
  }

  return false;
}

/**
 * @param {object} options
 * @param {object} options.editor - RTE wrapper from editorRef.current.editor
 * @param {string} [options.html] - HTML to insert (legacy / HTML callers)
 * @param {string} [options.text] - Plain text → inserted as TipTap `{ type: "text", text }`
 * @param {Function} [options.setTranscript] - Patient transcript setter
 * @param {Function} [options.onError] - Called if all strategies fail
 * @param {number|null} [options.insertPos] - Document position for insertContentAt
 * @param {boolean} [options.allowAppend=true] - If false, never append at document end
 * @returns {{ ok: boolean, reason?: string }}
 */
export function insertHtmlIntoReportEditor({
  editor,
  html,
  text = null,
  setTranscript,
  onError,
  insertPos = null,
  allowAppend = true,
}) {
  const useTextNode = text != null;
  const content = useTextNode
    ? toTipTapTextContent(text)
    : typeof html === "string"
      ? html
      : String(html ?? "");

  if (useTextNode) {
    if (!String(text).length) {
      return { ok: false, reason: "empty" };
    }
  } else if (!content || !String(content).trim()) {
    return { ok: false, reason: "empty" };
  }

  if (!editor) {
    return { ok: false, reason: "no-editor" };
  }

  queueMicrotask(() => {
    try {
      if (
        insertPos != null &&
        Number.isFinite(Number(insertPos)) &&
        editor?.chain &&
        editor?.state?.doc
      ) {
        const docSize = editor.state.doc.content.size;
        const pos = Math.max(0, Math.min(Number(insertPos), docSize));
        const insertedAt = editor
          .chain()
          .insertContentAt(pos, content, { updateSelection: false })
          .run();
        if (insertedAt !== false) {
          syncTranscript(editor, setTranscript);
          return;
        }
      }

      if (insertAtSelectionWithoutFocus(editor, content)) {
        syncTranscript(editor, setTranscript);
        return;
      }

      if (allowAppend && appendAtEndWithoutFocus(editor, content)) {
        syncTranscript(editor, setTranscript);
        return;
      }

      // HTML string fallback only — never stringify a text-node object into the doc.
      if (allowAppend && !useTextNode && appendViaSetContents(editor, content)) {
        syncTranscript(editor, setTranscript);
        return;
      }

      onError?.(new Error("No insert strategy succeeded"));
    } catch (err) {
      try {
        if (allowAppend && !useTextNode && appendViaSetContents(editor, content)) {
          syncTranscript(editor, setTranscript);
          return;
        }
      } catch (fallbackErr) {
        onError?.(fallbackErr);
        return;
      }
      onError?.(err);
    }
  });

  return { ok: true };
}
